import { useCallback, useEffect, useRef, useState } from "react";
import { Mail, Send, Upload, KeyRound, Trash2, Copy, FileText } from "lucide-react";
import { api, API, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const Card = ({ title, desc, icon: Icon, children, testid }) => (
  <div className="rounded-md border border-[#E5E7EB] bg-white p-5" data-testid={testid}>
    <div className="flex items-start gap-3">
      <Icon className="h-5 w-5 text-[#002FA7] mt-0.5" />
      <div>
        <h2 className="font-heading font-bold text-[#111827]">{title}</h2>
        <p className="text-xs text-[#6B7280] mt-0.5">{desc}</p>
      </div>
    </div>
    <div className="mt-5">{children}</div>
  </div>
);

export default function Automation() {
  const [users, setUsers] = useState([]);
  const [digest, setDigest] = useState(null);
  const [brand, setBrand] = useState(null);
  const [keys, setKeys] = useState([]);
  const [zones, setZones] = useState([]);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState("");
  const [keyName, setKeyName] = useState("");
  const [keyScope, setKeyScope] = useState("read_write");
  const headerRef = useRef(null);
  const footerRef = useRef(null);
  const signatureRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [u, d, b, k, h] = await Promise.all([
        api.get("/users"), api.get("/settings/digest"), api.get("/settings/branding"),
        api.get("/api-keys"), api.get("/digest/history"),
      ]);
      setUsers(u.data); setDigest(d.data); setBrand(b.data); setKeys(k.data); setHistory(h.data);
      const tz = await api.get("/timezones");
      setZones(tz.data.all);
    } catch (e) { toast.error(errMsg(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveDigest = async (next) => {
    setDigest(next);
    try {
      await api.put("/settings/digest", next);
      toast.success("Digest schedule saved");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveBrand = async (next) => {
    setBrand(next);
    try {
      await api.put("/settings/branding", next);
      toast.success("Report branding saved");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const sendNow = async () => {
    setBusy("digest");
    try {
      const { data } = await api.post("/digest/send-now");
      toast.success(`Digest sent to ${data.sent} recipient(s)`);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const uploadBrand = async (kind, file) => {
    if (!file) return;
    setBusy(kind);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/files/branding?kind=${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${kind === "header" ? "Header" : "Footer"} design uploaded`);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const createKey = async () => {
    if (!keyName.trim()) return toast.error("Give the integration a name");
    try {
      await api.post("/api-keys", { name: keyName.trim(), scope: keyScope });
      setKeyName("");
      toast.success("API key created");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const revokeKey = async (k) => {
    if (!window.confirm(`Revoke the key for ${k.name}?`)) return;
    try {
      await api.delete(`/api-keys/${k.key_id}`);
      toast.success("Key revoked");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const toggleRecipient = (email) => {
    const list = digest.recipients || [];
    const next = list.includes(email) ? list.filter((e) => e !== email) : [...list, email];
    saveDigest({ ...digest, recipients: next });
  };

  if (!digest || !brand) return <p className="text-sm text-[#6B7280]">Loading automation settings…</p>;

  return (
    <div data-testid="automation-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-[#111827]">Automation & Reports</h1>
      <p className="text-sm text-[#6B7280] mt-1">
        Weekly digest email, printable report branding, and API keys for connecting other lab applications.
      </p>

      <div className="grid xl:grid-cols-2 gap-4 mt-6">
        <Card title="Weekly Expiry & Stock Digest" desc="Pick which admin users receive it and when it goes out."
          icon={Mail} testid="digest-card">
          <div className="flex items-center justify-between">
            <Label className="text-xs uppercase tracking-[0.1em] text-[#6B7280]">Digest enabled</Label>
            <Switch checked={!!digest.enabled} onCheckedChange={(v) => saveDigest({ ...digest, enabled: v })}
              data-testid="digest-enabled-switch" />
          </div>

          <p className="text-xs uppercase tracking-[0.1em] text-[#6B7280] mt-5 mb-2">Recipients</p>
          <div className="space-y-2 max-h-48 overflow-y-auto rounded-md border border-[#E5E7EB] p-3">
            {users.length === 0 && <p className="text-xs text-[#6B7280]">No users found.</p>}
            {users.filter((u) => ["admin", "pathologist"].includes(u.role)).map((u) => (
              <label key={u.user_id} className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={(digest.recipients || []).includes(u.email)}
                  onCheckedChange={() => toggleRecipient(u.email)}
                  data-testid={`digest-recipient-${u.email}`} />
                <span className="text-[#111827]">{u.name}</span>
                <span className="text-xs text-[#6B7280]">{u.email} · {u.role}</span>
              </label>
            ))}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-[0.1em] text-[#6B7280]">Lab Timezone</Label>
              <Select value={digest.timezone || "Asia/Kolkata"} onValueChange={(v) => saveDigest({ ...digest, timezone: v })}>
                <SelectTrigger className="mt-1.5 bg-white" data-testid="digest-timezone-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white max-h-72">
                  {(zones.length ? zones : ["Asia/Kolkata", "UTC"]).map((z) => (
                    <SelectItem key={z} value={z}>{z}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-[0.1em] text-[#6B7280]">Day</Label>
              <Select value={String(digest.day_of_week)} onValueChange={(v) => saveDigest({ ...digest, day_of_week: Number(v) })}>
                <SelectTrigger className="mt-1.5 bg-white" data-testid="digest-day-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {DAYS.map((d, i) => (<SelectItem key={d} value={String(i)}>{d}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] text-[#6B7280]">
                Hour ({digest.timezone || "Asia/Kolkata"})
              </Label>
              <Input type="number" min={0} max={23} value={digest.hour} data-testid="digest-hour-input"
                onChange={(e) => setDigest({ ...digest, hour: Number(e.target.value) })}
                onBlur={() => saveDigest(digest)} className="mt-1.5" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] text-[#6B7280]">Expiry window (days)</Label>
              <Input type="number" min={1} max={365} value={digest.expiry_days} data-testid="digest-days-input"
                onChange={(e) => setDigest({ ...digest, expiry_days: Number(e.target.value) })}
                onBlur={() => saveDigest(digest)} className="mt-1.5" />
            </div>
          </div>

          <Button onClick={sendNow} disabled={busy === "digest"} data-testid="digest-send-now-btn"
            className="mt-4 bg-[#002FA7] hover:bg-[#00237C] text-white">
            <Send className="h-4 w-4 mr-2" /> {busy === "digest" ? "Sending…" : "Send digest now"}
          </Button>

          {history.length > 0 && (
            <div className="mt-5" data-testid="digest-history">
              <p className="text-xs uppercase tracking-[0.1em] text-[#6B7280] mb-2">Recent sends</p>
              {history.slice(0, 4).map((h) => (
                <p key={h.run_id} className="text-xs text-[#111827]">
                  {String(h.at).slice(0, 16).replace("T", " ")} — {h.sent} sent ·
                  {" "}{h.counts?.expired}/{h.counts?.expiring_soon}/{h.counts?.low_stock} expired/expiring/low
                </p>
              ))}
            </div>
          )}
        </Card>

        <Card title="Printable Report Branding" desc="Upload your letterhead, footer and the pathologist's signature used on signed patient reports."
          icon={FileText} testid="branding-card">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] text-[#6B7280]">Lab Name (fallback header)</Label>
              <Input value={brand.lab_name} data-testid="brand-lab-name"
                onChange={(e) => setBrand({ ...brand, lab_name: e.target.value })}
                onBlur={() => saveBrand(brand)} className="mt-1.5" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] text-[#6B7280]">Subtitle / Accreditation</Label>
              <Input value={brand.subtitle} data-testid="brand-subtitle"
                onChange={(e) => setBrand({ ...brand, subtitle: e.target.value })}
                onBlur={() => saveBrand(brand)} className="mt-1.5" />
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-3 mt-4">
            {[["header", headerRef], ["footer", footerRef], ["signature", signatureRef]].map(([kind, ref]) => (
              <div key={kind}>
                <Label className="text-xs uppercase tracking-[0.1em] text-[#6B7280]">{kind} design</Label>
                <input ref={ref} type="file" accept="image/*" className="hidden" data-testid={`brand-${kind}-input`}
                  onChange={(e) => { uploadBrand(kind, e.target.files?.[0]); e.target.value = ""; }} />
                <button type="button" onClick={() => ref.current?.click()} data-testid={`brand-${kind}-btn`}
                  className="mt-1.5 w-full flex items-center justify-center gap-2 rounded-md border border-dashed border-[#E5E7EB] bg-[#F3F4F6] px-3 py-3 text-sm text-[#6B7280] hover:border-[#002FA7] hover:text-[#002FA7]">
                  <Upload className="h-4 w-4" /> {busy === kind ? "Uploading…" : `Upload ${kind} image`}
                </button>
                {brand[`${kind}_file_id`] && (
                  <a href={`${API}/files/${brand[`${kind}_file_id`]}`} target="_blank" rel="noreferrer"
                    data-testid={`brand-${kind}-preview`} className="text-xs text-[#002FA7] hover:underline mt-1 inline-block">
                    Current {kind} uploaded — view
                  </a>
                )}
              </div>
            ))}
          </div>

          <div className="grid sm:grid-cols-3 gap-3 mt-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] text-[#6B7280]">Verifiers</Label>
              <Select value={String(brand.verifier_count)} onValueChange={(v) => saveBrand({ ...brand, verifier_count: Number(v) })}>
                <SelectTrigger className="mt-1.5 bg-white" data-testid="brand-verifier-count"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="1">One pathologist</SelectItem>
                  <SelectItem value="2">Two pathologists</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] text-[#6B7280]">Verifier 1</Label>
              <Input value={brand.verifier_one} data-testid="brand-verifier-one"
                onChange={(e) => setBrand({ ...brand, verifier_one: e.target.value })}
                onBlur={() => saveBrand(brand)} className="mt-1.5" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] text-[#6B7280]">Verifier 2</Label>
              <Input value={brand.verifier_two} data-testid="brand-verifier-two"
                onChange={(e) => setBrand({ ...brand, verifier_two: e.target.value })}
                onBlur={() => saveBrand(brand)} className="mt-1.5" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] text-[#6B7280]">Verifier 1 qualification</Label>
              <Input value={brand.verifier_one_qual || ""} data-testid="brand-verifier-one-qual"
                onChange={(e) => setBrand({ ...brand, verifier_one_qual: e.target.value })}
                onBlur={() => saveBrand(brand)} className="mt-1.5"
                placeholder="MD (Pathology), Reg. 12345" />
            </div>
          </div>
        </Card>

        <Card title="Integration API Keys" desc="Let report-interpretation or ISO compliance apps read and write LabTrack data."
          icon={KeyRound} testid="apikeys-card">
          <div className="flex flex-col sm:flex-row gap-2">
            <Input value={keyName} onChange={(e) => setKeyName(e.target.value)} placeholder="e.g. ISO Compliance Checker"
              data-testid="apikey-name-input" />
            <Select value={keyScope} onValueChange={setKeyScope}>
              <SelectTrigger className="sm:w-40 bg-white" data-testid="apikey-scope-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="read_only">Read only</SelectItem>
                <SelectItem value="read_write">Read & write</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={createKey} data-testid="apikey-create-btn" className="bg-[#002FA7] hover:bg-[#00237C] text-white">
              Create key
            </Button>
          </div>

          <div className="mt-4 space-y-2">
            {keys.length === 0 && <p className="text-xs text-[#6B7280]">No integration keys yet.</p>}
            {keys.map((k) => (
              <div key={k.key_id} className="rounded-md border border-[#E5E7EB] p-3" data-testid={`apikey-${k.key_id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-[#111827]">{k.name}</span>
                  <span className="text-[10px] uppercase tracking-[0.1em] text-[#6B7280]">{k.scope}</span>
                  <button onClick={() => revokeKey(k)} data-testid={`apikey-revoke-${k.key_id}`}
                    className="text-[#C92A2A] p-1 rounded hover:bg-[#F3F4F6]"><Trash2 className="h-4 w-4" /></button>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <code className="text-xs bg-[#F3F4F6] rounded px-2 py-1 truncate flex-1">{k.key}</code>
                  <button onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(k.key);
                      toast.success("Key copied");
                    } catch (e) {
                      toast.error("Copy blocked by the browser — select the key text manually");
                    }
                  }}
                    data-testid={`apikey-copy-${k.key_id}`} className="p-1 rounded hover:bg-[#F3F4F6] text-[#002FA7]">
                    <Copy className="h-4 w-4" />
                  </button>
                </div>
                <p className="text-[10px] text-[#6B7280] mt-1">
                  Last used: {k.last_used_at ? String(k.last_used_at).slice(0, 16).replace("T", " ") : "never"}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-5 rounded-md bg-[#F3F4F6] p-3">
            <p className="text-xs uppercase tracking-[0.1em] text-[#6B7280] mb-2">How other apps connect</p>
            <p className="text-xs text-[#111827]">
              Send the key as the <code>X-API-Key</code> header to any LabTrack endpoint, e.g.
            </p>
            <code className="block text-[11px] bg-white rounded p-2 mt-2 overflow-x-auto">
              GET /api/records/samples · POST /api/records/qc · GET /api/integrations/compliance · GET /api/integrations/schema
            </code>
          </div>
        </Card>
      </div>
    </div>
  );
}
